/**
 * Schémas Zod — Files (uploads PDF & attachments).
 *
 * Pourquoi 2 schemas distincts (Initiate vs Complete) ?
 * -----------------------------------------------------
 * Le flow d'upload est en 2 temps pour garder la mémoire de l'API basse et
 * profiter de l'upload direct vers Supabase Storage :
 *
 *  1. POST /v1/files/uploads/initiate  →  l'API valide les méta + crée une
 *     row `app.files` en status='pending' + retourne une signed URL d'upload
 *     direct vers le bucket Storage.
 *  2. Le client PUT le binaire vers la signed URL (ne passe PAS par l'API).
 *  3. POST /v1/files/uploads/:id/complete  →  l'API vérifie que le binaire
 *     est bien arrivé (HEAD object), met le status à 'ready', recompute
 *     éventuellement le sha256 et le size_bytes côté serveur.
 *
 * Ce design est ce que recommande Supabase pour les uploads >5MB (PDF d'AC
 * font typiquement 1-15MB, certains 30MB+).
 */

import { z } from "zod";

import { LIMITS } from "../constants.js";

// -----------------------------------------------------------------------------
// File kind — type métier du fichier dans le domaine d'AC
// -----------------------------------------------------------------------------
//
// 'submission'      → PDF de cotation/renouvellement reçu d'un assureur
// 'client-request'  → formulaire signé / pièce envoyée par le client
// 'attachment'      → pièce jointe libre (note, brochure, courriel imprimé)
// -----------------------------------------------------------------------------
// 9.U.3 — `form_template_source` : PDF téléversé comme SOURCE d'une génération de
// modèle de formulaire. Seul kind autorisé SANS parent (project/submission/
// company/intake_event) : le modèle n'existe pas encore au moment de l'upload et
// un modèle n'appartient ni à un dossier ni à une entreprise. L'exemption est
// portée par le check applicatif de `POST /v1/files/uploads/initiate` et reste
// bornée à ce kind ; le tenant scoping, le MIME et la taille sont inchangés.
// 9.BK.2 — `census_source` : le classeur d'employés déposé par le conseiller.
// C'est le SEUL kind qui accepte autre chose qu'un PDF, et le seul que le MIME
// de classeur accepte : le couplage va dans les deux sens (cf.
// `FILES_UPLOAD_KIND_MIME_MISMATCH`), sinon élargir le MIME ici ouvrirait le
// dépôt de classeurs sur tous les kinds — y compris ceux qui finissent en
// pièce jointe d'un courriel à un assureur.
export const FILE_KINDS = [
  "submission",
  "client-request",
  "attachment",
  "form_template_source",
  "census_source",
  // 9.BK.3 — la projection de l'effectif transmise à un assureur. Mêmes données
  // nominatives que `census_source`, donc même régime : réservé au cabinet.
  "census_export",
] as const;

/** Le MIME d'un classeur `.xlsx`. Nommé une fois, comparé partout. */
export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" as const;
export type FileKind = (typeof FILE_KINDS)[number];

// -----------------------------------------------------------------------------
// File status — cycle de vie côté DB (champ `status` ajouté par migration 0013)
// -----------------------------------------------------------------------------
//
// 'pending'   → row créée, signed URL retournée, binaire pas encore uploadé
// 'ready'     → binaire confirmé en bucket, prêt pour extraction
// 'errored'   → upload abandonné / corrompu (TTL de cleanup en cron)
// -----------------------------------------------------------------------------
export const FILE_STATUSES = ["pending", "ready", "errored"] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

// -----------------------------------------------------------------------------
// Initiate upload — prépare la signed URL.
//
// On exige AU MOINS UN parent (project_id, submission_id, ou company_id) pour
// éviter les fichiers orphelins. La règle est ré-appliquée côté DB par les
// CHECKs et les RLS.
//
// 9.K.12 (D58.1-b refines schemas) — split base/refined :
//   `InitiateFileUploadBaseSchema` (sans refine) est passé à zValidator côté
//   route API (`apps/api/src/routes/files.ts`). Le check "≥1 parent" est
//   appliqué dans le handler via `bilingualHttpError("FILES_UPLOAD_PARENT_REQUIRED", 400)`
//   pour porter le code bilingue ADR 0027 (P-3) au lieu d'un `validation_error`
//   Zod générique. Pattern parité 9.K.1/9.K.2 (lookup) et 9.F.1 (document-links).
//   Le wording du `.refine()` ci-dessous est en EN technique (P-2 ADR 0027) ;
//   il ne sert que de fallback dev quand le schema refined est utilisé
//   directement (tests ou consommateur strict). Le wording user-facing
//   FR/EN vit dans `ERROR_CODES.FILES_UPLOAD_PARENT_REQUIRED`.
// -----------------------------------------------------------------------------
export const InitiateFileUploadBaseSchema = z.object({
  project_id: z.string().uuid().optional(),
  submission_id: z.string().uuid().optional(),
  company_id: z.string().uuid().optional(),
  /**
   * 9.T.3 — 4e parent legitimate. Permet aux brokers d'uploader un PDF reçu
   * en créant atomiquement un `intake_event` orphan (sans project/submission/
   * company) tant que la résolution manuelle (ResolveDialog ou 9.T.4
   * materialize) n'a pas eu lieu. Migration 0044 ajoute la colonne DB
   * + index partial. Le check applicatif `FILES_UPLOAD_PARENT_REQUIRED`
   * accepte désormais ce 4e parent comme valide.
   */
  intake_event_id: z.string().uuid().optional(),

  kind: z.enum(FILE_KINDS),
  file_name: z.string().min(1).max(255),
  size_bytes: z
    .number()
    .int()
    .positive()
    .max(LIMITS.PDF_MAX_SIZE_MB * 1024 * 1024),
  // Le couplage kind ⇄ MIME est vérifié dans le handler, pour porter un code
  // bilingue plutôt qu'un `validation_error` générique — motif 9.K.12.
  mime_type: z.union([z.literal("application/pdf"), z.literal(XLSX_MIME)]),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/, "sha256 must be a 64-char hex string")
    .optional(),
});

export const InitiateFileUploadSchema = InitiateFileUploadBaseSchema.refine(
  (v) => Boolean(v.project_id || v.submission_id || v.company_id || v.intake_event_id),
  {
    // 9.K.12 — refine technique EN (P-2 ADR 0027). Wording user-facing FR/EN
    // dans ERROR_CODES.FILES_UPLOAD_PARENT_REQUIRED.
    // 9.T.3 — accepte désormais intake_event_id comme 4e parent légal.
    message:
      "At least one parent required: project_id, submission_id, company_id, or intake_event_id",
  },
);
export type InitiateFileUploadInput = z.infer<typeof InitiateFileUploadSchema>;

// -----------------------------------------------------------------------------
// Complete upload — confirme que le binaire est arrivé.
// -----------------------------------------------------------------------------
export const CompleteFileUploadSchema = z.object({
  // Le client peut renvoyer le sha256 calculé après upload (si pas envoyé
  // initialement) — l'API peut le valider contre l'objet en bucket.
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
});
export type CompleteFileUploadInput = z.infer<typeof CompleteFileUploadSchema>;

// -----------------------------------------------------------------------------
// Listing
// -----------------------------------------------------------------------------
export const FileFilterSchema = z.object({
  project_id: z.string().uuid().optional(),
  submission_id: z.string().uuid().optional(),
  company_id: z.string().uuid().optional(),
  kind: z.enum(FILE_KINDS).optional(),
  status: z.enum(FILE_STATUSES).optional(),
});
export type FileFilter = z.infer<typeof FileFilterSchema>;

/** Optional read-only context for generated documents on broker file listings. */
export const FileDocumentContextSchema = z.object({
  request_title: z.string().nullable(),
  template_name: z.string().nullable(),
  version_number: z.number().int().positive().nullable(),
});
export type FileDocumentContext = z.infer<typeof FileDocumentContextSchema>;
